import type paper from "paper";
import { isVisible, type Project } from "../model/Project";
import { pieceDisplayOffset } from "../model/pieceTransform";

/**
 * Which fallback a draw gesture uses when its anchor point is not strictly
 * inside any part:
 *  - `"containment"` — no fallback; the point must land inside a part
 *    (StitchTool: a seam vertex must be on leather; HardwareTool: a hole must
 *    be drilled inside the outline).
 *  - `"bbox"` — a bounding-box overlap with the gesture's `overlapBounds` is
 *    enough, so a hole/stamp drawn just off an edge still attaches (HoleTool,
 *    StampTool, StitchTool's seam bbox).
 *  - `"intersection"` — like `"bbox"` for picking the candidate, but the
 *    resolved part must then REALLY overlap the gesture shape (its centre
 *    inside, or a non-empty geometric intersection of `overlapPathData` with
 *    the outline), so a cutout drawn near — but not over — a part is rejected
 *    (#779, CutoutTool).
 */
export type DrawTargetOverlapPolicy = "containment" | "bbox" | "intersection";

/**
 * Resolve which part a draw gesture (hole / cutout / stitch / stamp / hardware)
 * should attach to at `pt` (#783). The old behaviour walked `project.pieces` in
 * array order and took the FIRST outline that contained the point — the
 * BOTTOM-most part — and didn't skip hidden parts, so drawing over stacked
 * parts landed on the wrong (often hidden) one.
 *
 * Rules, in order:
 *  1. never a hidden part;
 *  2. the **selected** part, when `pt` lands on it (so you can aim at a specific
 *     part by selecting it first);
 *  3. otherwise the **top-most** visible part whose outline contains `pt` (later
 *     in `pieces` = drawn on top);
 *  4. if the policy allows a fallback and nothing contains `pt`, the same
 *     preference but by bounding-box overlap with `overlapBounds` — and, under
 *     the `"intersection"` policy, only if that part is really overlapped by
 *     `overlapPathData` (else no target at all).
 *
 * The default policy is `"bbox"`, which preserves the pre-#812 behaviour of the
 * original callers (bbox fallback when `overlapBounds` is given, containment
 * only when it is not).
 *
 * Spread-view mapping (#786): `pt` / `overlapBounds` / `overlapPathData` are all
 * in DISPLAYED space. A linked part shown in Spread view is drawn `offset` away
 * from its model geometry (`pieceDisplayOffset`), so each part's outline is
 * translated into displayed space before it is hit-tested — otherwise a click
 * over a moved part would miss. The resolved part's `offset` is returned so the
 * caller can map the displayed anchor back into ORIGINAL space before storing
 * it (the renderer re-applies the offset, which would otherwise double it).
 *
 * Returns the target piece id and its displayed offset, or null when the
 * gesture is over no visible part.
 */
export interface DrawTargetResolution {
  pieceId: string;
  /** Resolved part's displayed offset: `displayed = original + offset`. Zero in
   *  Assembled view and for unlinked parts. Subtract from a displayed cursor
   *  point to get the ORIGINAL-space coordinate to store (#786). */
  offset: { dx: number; dy: number };
}

export function resolveDrawTarget(
  project: Project,
  pt: { x: number; y: number },
  scope: paper.PaperScope,
  selectedPieceId: string | null | undefined,
  partLayoutView: "spread" | "assembled",
  overlapBounds?: {
    x: number;
    y: number;
    width: number;
    height: number;
  } | null,
  opts?: {
    policy?: DrawTargetOverlapPolicy;
    /** Real gesture geometry for the `"intersection"` policy's overlap gate. */
    overlapPathData?: string | null;
  },
): DrawTargetResolution | null {
  const policy = opts?.policy ?? "bbox";
  const P = new scope.Point(pt.x, pt.y);
  // Containment never uses a fallback rect, even if bounds were supplied.
  const rect =
    policy !== "containment" && overlapBounds
      ? new scope.Rectangle(overlapBounds)
      : null;

  // Test each visible part once (parse → translate into displayed space →
  // contains / bbox-overlap → discard).
  const results = project.pieces.filter(isVisible).map((piece) => {
    const offset = pieceDisplayOffset(piece, partLayoutView);
    let path: paper.PathItem | null = null;
    try {
      path = new scope.CompoundPath({
        pathData: piece.shapePathData,
        insert: false,
      });
    } catch {
      return { id: piece.id, offset, contains: false, overlaps: false };
    }
    if (offset.dx !== 0 || offset.dy !== 0) {
      path.translate(new scope.Point(offset.dx, offset.dy));
    }
    const contains = path.contains(P);
    const overlaps = rect ? path.bounds.intersects(rect) : false;
    path.remove();
    return { id: piece.id, offset, contains, overlaps };
  });

  const sel = selectedPieceId
    ? results.find((r) => r.id === selectedPieceId)
    : undefined;

  // Selected part wins when the gesture actually lands on it.
  if (sel?.contains) return { pieceId: sel.id, offset: sel.offset };
  // Top-most visible part containing the point.
  for (let i = results.length - 1; i >= 0; i--) {
    const r = results[i]!;
    if (r.contains) return { pieceId: r.id, offset: r.offset };
  }
  // Bounding-box fallback (selected first, then top-most).
  if (rect) {
    let candidate: (typeof results)[number] | null = null;
    if (sel?.overlaps) candidate = sel;
    else {
      for (let i = results.length - 1; i >= 0; i--) {
        if (results[i]!.overlaps) {
          candidate = results[i]!;
          break;
        }
      }
    }
    if (candidate) {
      // Under `"intersection"`, the bbox candidate must be REALLY overlapped by
      // the gesture shape — a bbox brush is too loose for a cutout (#779).
      if (policy === "intersection") {
        const piece = project.pieces.find((p) => p.id === candidate!.id);
        if (
          piece &&
          pieceReallyOverlapped(
            scope,
            piece.shapePathData,
            candidate.offset,
            P,
            opts?.overlapPathData,
          )
        ) {
          return { pieceId: candidate.id, offset: candidate.offset };
        }
        return null;
      }
      return { pieceId: candidate.id, offset: candidate.offset };
    }
  }
  return null;
}

/** Map a DISPLAYED-space point into a part's ORIGINAL (construction) space using
 *  the offset from {@link resolveDrawTarget} — the coordinate a created feature
 *  must be stored at so the render translate doesn't double-offset it (#786). */
export function toOriginalSpace<T extends { x: number; y: number }>(
  pt: T,
  offset: { dx: number; dy: number },
): { x: number; y: number } {
  return { x: pt.x - offset.dx, y: pt.y - offset.dy };
}

/** Whether the gesture really overlaps the part — its anchor `P` inside the
 *  outline, or a non-empty geometric intersection of `overlapPathData` with the
 *  outline (not just touching bounding boxes). Keeps a cutout drawn near — but
 *  not over — a part from attaching (#779). */
function pieceReallyOverlapped(
  scope: paper.PaperScope,
  piecePathData: string,
  offset: { dx: number; dy: number },
  P: paper.Point,
  overlapPathData: string | null | undefined,
): boolean {
  let partPath: paper.PathItem;
  try {
    partPath = new scope.CompoundPath({
      pathData: piecePathData,
      insert: false,
    });
  } catch {
    return false;
  }
  // `P` / `overlapPathData` are in displayed space; move the part's outline
  // into displayed space too before comparing (#786).
  if (offset.dx !== 0 || offset.dy !== 0) {
    partPath.translate(new scope.Point(offset.dx, offset.dy));
  }
  let overlaps = partPath.contains(P);
  if (!overlaps && overlapPathData) {
    try {
      const gesturePath = new scope.CompoundPath({
        pathData: overlapPathData,
        insert: false,
      });
      const inter = partPath.intersect(gesturePath, { insert: false });
      overlaps = inter ? !inter.isEmpty() : false;
      inter?.remove();
      gesturePath.remove();
    } catch {
      overlaps = false;
    }
  }
  partPath.remove();
  return overlaps;
}
